import { z } from "zod";
import {
  operatorNeedsContextSchema,
  REMOTE_DESKTOP_EXECUTE_MODEL_TIERS,
} from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";

export const remoteDesktopExecuteStep = defineStepConfig({
  type: "remote_desktop_execute",
  name: "Desktop Computer Use",
  description:
    "Execute a natural-language instruction on the current remote desktop session via computer use. Requires an active session created by a Login to Remote Desktop step earlier in the run.",
  category: "Computer Use",

  inputSchema: z.object({
    instruction: z
      .string()
      .min(1)
      .describe(
        `Task for the operator. The operator is a fresh computer-use agent that sees ONLY the current screen and this text — no other workflow context. Write a self-contained, imperative task:
- Include the goal, every concrete value needed (IDs, names, amounts), and an explicit done-condition.
- Scope one coherent unit of work per step (a full form, row, or search-and-report), not a single keystroke.
- Phrase assumptions about the current screen as checks ("If X is not open, open it first"), never as facts.
- Describe click targets visually ("the funnel icon on the SKU column header"), never by pixel coordinates.`,
      ),
    sessionId: z
      .string()
      .optional()
      .describe("Optional explicit session ID override"),
    maxIterations: z
      .number()
      .int()
      .positive()
      .max(200)
      .optional()
      .describe("Maximum number of computer-use iterations"),
    resumeSummary: z
      .string()
      .max(2000)
      .optional()
      .describe(
        "Recap of a prior operator run in this session: what it did and its final `Current screen state:` line. Pass on any follow-up run for continuity; when resuming after `needsContext`, pass its `operatorSummary`.",
      ),
  }),

  outputSchema: z.object({
    success: z.boolean(),
    message: z
      .string()
      .nullable()
      .describe(
        "The operator's final report. Ends with a `Current screen state:` line that downstream steps can pass as `resumeSummary`.",
      ),
    error: z.string().nullable(),
    iterationsUsed: z.number().nullable(),
    needsContext: operatorNeedsContextSchema
      .nullable()
      .describe(
        "Set when the operator halted to request information only the orchestrating agent holds.",
      ),
  }),

  configSchema: z.object({
    /**
     * Computer-use model tier, always explicit on a workflow node.
     *
     * This used to be optional and fall through to the orchestrating agent
     * definition's tier, which made the effective model invisible from the
     * workflow editor — a node left alone silently ran whatever the definition
     * said. The default is applied when the stored config is re-parsed at run
     * time, so every node carries a concrete tier without a backfill. The
     * agent-TOOL path still inherits from the definition: that tool's config
     * schema has no `model` key, so it is the only configuration surface there.
     */
    model: z.enum(REMOTE_DESKTOP_EXECUTE_MODEL_TIERS).default("sonnet"),
    /**
     * In-desktop application credentials (referenced by id). Resolved by the
     * step handler and passed to the operator so it can log into apps running
     * inside the remote desktop. When omitted, falls back to the orchestrating
     * agent definition's configured application credentials.
     */
    applicationCredentials: z
      .object({
        credentialIds: z.array(z.string().uuid()).default([]),
      })
      .optional(),
    /**
     * Procedure (SOP) ids this step may replay via `run_procedure`, folded into
     * the orchestrating agent definition's own allowlist (a non-empty list
     * enables procedures even when the definition has them off — mirrors the
     * background-agent node's `procedures`). Replayed visually: the remote
     * desktop is a streamed canvas, so steps run from the screen, and a
     * natural-language procedure hardens into deterministic coordinate clicks
     * over successful runs.
     */
    procedures: z.array(z.string().uuid()).optional(),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
